import { useEffect, useMemo } from 'react';
import { useLazyQuery } from '@apollo/client/react';

import { CUSTOMER_WITH_FULL_ACCOUNTS } from 'queries/customer';
import { DEBT_SALE_STATUS } from 'utilities/constants';

/*
  Since we're having to load full accounts at the application level
  and currently still need SimpleAccount for some account details
  this seems like the cleanest way to take advantage of cache and reduce 
  code duplication
*/

/** TODO:
 * we're using this dozens of times in the app which means we have dozens of instances of this state all running in useMemo
 * I think this should be refactored into a Provider similar to CustomerInfoProvider
 */
const useAccountQuery = (customerId, accountId, options = {}) => {
  const [getAccounts, accountQuery] = useLazyQuery(
    CUSTOMER_WITH_FULL_ACCOUNTS,
    {
      ...options,
    },
  );
  const data = accountQuery.data;

  const values = useMemo(() => {
    const simpleAccounts = data?.customer?.accounts || [];
    const simpleAccount =
      simpleAccounts.find((account) => account.id === accountId) || {};

    const accounts = data?.customer?.creditAccounts || [];
    const account =
      accounts.find((creditAccount) => creditAccount.id === accountId) || {};

    const isDebtSold = (account) =>
      account?.accountDetails?.debtSaleInfo?.status === DEBT_SALE_STATUS.SOLD;

    const accountInBankruptcy = account?.statuses?.isBankrupt;
    const accountInDebtSale = isDebtSold(account);
    const customerInDebtSale = accounts.some(isDebtSold);

    const contactInfo = data?.customer?.contactInfo || {};
    const statuses = account?.statuses?.raw || [];

    return {
      account,
      accountInBankruptcy,
      accountInDebtSale,
      accounts,
      contactInfo,
      customerInDebtSale,
      simpleAccount,
      simpleAccounts,
      statuses,
    };
  }, [accountId, data]);

  useEffect(() => {
    if (customerId) {
      getAccounts({ variables: { customerId } });
    }
  }, [customerId]);

  return {
    accountQuery,
    ...values,
  };
};

export default useAccountQuery;
